import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { FaPlus } from "react-icons/fa6";
import { appraisalState, getAllAppraisals } from "./appraisalSlice";
import { allUsers } from "../user/userSlice";
import { getTemplates } from "../template/templateSlice";
import GridTable from "../../components/GridTable";
import Loader from "../../components/Loader";
import Dropdown from "../../components/Dropdown";
import { showNotification } from "../../components/Notification";
import { deleteAppraisal, getAllAppraisal } from "../../api/appraisalApi";
import { getAllUsers } from "../../api/userApi";
import { getAllTemplates } from "../../api/templateApi";
import { formatDateToDDMMYYYY } from "../../uitls/Utils";

const AppraisalReviewed = () => {
  const [submitReferenceState, setSubmitReferenceState] = useState();
  const [templateOptions, setTemplateOptions] = useState();
  const [employeeOptions, setEmployeeOptions] = useState();
  const [formValues, setFormValues] = useState();
  const dispatch = useDispatch();
  const naviagte = useNavigate();

  const allAppraisals = useSelector(getAllAppraisals);
  const stateOfAppraisal = useSelector(appraisalState);
  const allEmployees = useSelector(allUsers);
  const allTemplates = useSelector(getTemplates);

  useEffect(() => {
    dispatch(getAllAppraisal());
    dispatch(getAllUsers());
    dispatch(getAllTemplates());
  }, [dispatch]);

  useEffect(() => {
    setSubmitReferenceState(false);
  }, [submitReferenceState]);

  useEffect(() => {
    const employeesList = allEmployees
      ?.filter((employee) => !employee?.isDeleted)
      ?.map((employee) => ({
        label: employee?.firstName,
        value: employee?.id,
      }));
    setEmployeeOptions(employeesList);
    const templatesList = allTemplates?.map((template) => ({
      label: template?.templateTitle,
      value: template?.id,
    }));
    setTemplateOptions(templatesList);
  }, [allEmployees, allTemplates]);

  const columns = [
    {
      field: "employeeName",
      headerName: "Employee Name",
      width: 200,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "templateName",
      headerName: "Template Name",
      width: 300,
      headerClassName: "super-app-theme--header",
    },

    {
      field: "createdBy",
      headerName: "Created By",
      width: 200,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "createdDate",
      headerName: "Created Date",
      width: 200,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "updatedDate",
      headerName: "Updated Date",
      width: 200,
      headerClassName: "super-app-theme--header",
    },
  ];

  // Format rows directly from Redux state with filtering
  const formattedRows = React.useMemo(() => {
    if (!Array.isArray(allAppraisals)) return [];

    const { employee, template } = formValues || {};

    // Apply filters if they exist
    let filteredAppraisals = allAppraisals;
    if (employee?.value || template?.value) {
      filteredAppraisals = allAppraisals.filter((appraisal) => {
        const isEmployeeMatch = employee?.value
          ? appraisal?.user?.id === employee.value
          : true;
        const isTemplateMatch = template?.value
          ? appraisal?.appraisalTemplate?.id === template.value
          : true;
        return isEmployeeMatch && isTemplateMatch;
      });
    }

    // Sort by createdDate descending (newest first)
    const sorted = filteredAppraisals.sort((a, b) => {
      const dateA = new Date(a.createdDate || 0);
      const dateB = new Date(b.createdDate || 0);
      return dateB - dateA; // Descending order (newest first)
    });
    
    return sorted.map((appraisal, index) => ({
      ...appraisal,
      s_no: index + 1,
      employeeName: `${appraisal?.user?.firstName} ${appraisal?.user?.lastName}`,
      templateName: appraisal?.appraisalTemplate?.templateTitle,
      createdBy: allEmployees?.find(
        (employee) => employee?.id === appraisal?.createdBy
      )?.firstName,
      createdDate: formatDateToDDMMYYYY(appraisal?.createdDate || null),
      updatedDate: formatDateToDDMMYYYY(appraisal?.updatedDate || null),
    }));
  }, [allAppraisals, allEmployees, formValues]);

  const trimObjectValues = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(trimObjectValues);
    } else if (typeof obj === "object" && obj !== null) {
      return Object.keys(obj).reduce((acc, key) => {
        acc[key] = trimObjectValues(obj[key]);
        return acc;
      }, {});
    } else if (typeof obj === "string") {
      return obj.trim();
    }
    return obj;
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      titleText: "Delete Appraisal ?",
      text: "Are you sure want to delete this appraisal?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      customClass: {
        popup: "custom-swal-width", // Custom width of the popup
        icon: "custom-swal-icon", // Custom icon size
        title: "custom-swal-title", // Custom title size
        content: "custom-swal-content", // Custom content (text) size
      },
    });

    if (result.isConfirmed) {
      try {
        await dispatch(deleteAppraisal(row.id)).unwrap();
        showNotification("success", "Appraisal deleted successfully");
      } catch (error) {
        const errorMessage = error?.message || "Failed to delete appraisal";
        showNotification("error", errorMessage);
      }
    }
  };

  const handleEdit = (row) => {
    naviagte("/mgr/update-appraisals/?id=" + row.id);
  };
  const handleDropdownChange = (name) => (option) => {
    setFormValues((prevValues) => ({ ...prevValues, [name]: option }));
  };

  const handleView = (row) => {
    naviagte("/mgr/view-appraisals/?id=" + row.id);
  };
  const handleReview = (row) => {
    naviagte("/mgr/review-appraisals/?id=" + row.id);
  };
  const onClickAction = (actionType, row, id) => {
    if (actionType === "VIEW") {
      handleView(row);
    } else if (actionType === "EDIT") {
      handleEdit(row);
    } else if (actionType === "DELETE") {
      handleDelete(row);
    } else if (actionType === "REVIEW") {
      handleReview(row);
    }
  };

  return (
    <>
      {stateOfAppraisal?.loading && <Loader />}
      <div className="rounded-sm border border-none bg-white pt-4 pl-4">
        <div className="grid grid-cols-3 gap-x-4">
          <div className="max-w-xs">
            <Dropdown
              submitRef={true}
              defaultValue={formValues?.employee || null}
              options={employeeOptions}
              handleChange={handleDropdownChange("employee")}
              errorMessage={""}
              requiredField={false}
              defaultLabel="Filter by employee name"
              label=""
              className="h-[39px]"
              isClearable={true}
            />
          </div>
          <div className="max-w-xs">
            <Dropdown
              submitRef={true}
              defaultValue={formValues?.template || null}
              options={templateOptions}
              handleChange={handleDropdownChange("template")}
              errorMessage={""}
              requiredField={false}
              defaultLabel="Filter by template Name"
              label=""
              className="h-[39px]"
              isClearable={true}
            />
          </div>
          <button
            type="button"
            className="cursor-pointer h-9 text-white flex items-center justify-center bg-primary border border-white rounded-lg w-fit pl-2 pr-2"
            onClick={() => {
              naviagte("/mgr/create-appraisal");
            }}
            aria-label="Create New Appraisal"
          >
            <span className="mr-1">Create New Appraisal</span>
            <FaPlus className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="mb-5">
        <GridTable
          onClickAction={onClickAction}
          actions={["EDIT", "VIEW", "DELETE", "REVIEW"]}
          rowData={formattedRows}
          columnData={columns}
          toolTipName={"Create Staff"}
          hideSearchBar={true}
        />
      </div>
    </>
  );
};

export default AppraisalReviewed;
